import { Button, ButtonProps, Text } from "@chakra-ui/react";
import { FC, useState } from "react";
import writeXlsxFile from "write-excel-file";

import { AddIcon } from "@nens/ranalib";
import * as api from "@nens/threedi/openapi-client";

import ExportLoadingModal from "../schematisations/ExportLoadingModal";

import { UsageRow } from "./Usage";

interface UsageRowsExcelButtonProps extends ButtonProps {
  usageQuery: (limit: number, offset: number) => Promise<api.PaginatedUsageList>;
  organisationUniqueId: string;
}

const excelFileSchema = [
  {
    column: "Simulation name",
    value: (row: UsageRow) => row.simulation!.name,
    width: 80,
  },
  {
    column: "uuid",
    value: (row: UsageRow) => row.uuid,
    width: 40,
  },
  {
    column: "Model ID",
    value: (row: UsageRow) => row.simulation!.threedimodelId!,
    width: 80,
  },
  {
    column: "Username",
    value: (row: UsageRow) => row.userName,
    width: 20,
  },
  {
    column: "Status",
    value: (row: UsageRow) => row.status,
    width: 15,
  },
  {
    column: "Type",
    value: (row: UsageRow) => row.simulationType,
    width: 10,
  },
  {
    column: "Tags",
    value: (row: UsageRow) => row.simulation?.tags,
    width: 10,
  },
  {
    column: "Started",
    //value: (row: UsageRow) => row.started?.toLocaleDateString(),
    value: (row: UsageRow) => (row.started ? new Date(row.started) : null),
    type: Date,
    width: 10,
  },
  {
    // To show simulation length in a useful way, put it as a number in hours and format as time
    column: "Length",
    value: (row: UsageRow) =>
      (row.totalTime ?? null) === null ? null : (row.totalTime ?? 0) / (60 * 60 * 24),
    type: Number,
    format: "[h]:mm:ss",
    width: 10,
  },
  {
    column: "Project",
    value: (row: UsageRow) =>
      row.simulation?.tags?.filter((tag) => tag.split(":")[0] === "project"),
    width: 10,
  },
];

const UsageExcelButton: FC<UsageRowsExcelButtonProps> = ({
  usageQuery,
  disabled,
  organisationUniqueId,
  ...rest
}) => {
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);

  let fetchedAllUsages = false;
  const usageRows: UsageRow[] = [];
  const limit = 250;
  let offset = 0;

  let usage: api.PaginatedUsageList | undefined;
  const fetchAllUsageRows = async () => {
    while (!fetchedAllUsages) {
      usage = await usageQuery(limit, offset);
      usage.results.map((row) => usageRows.push({ ...row, uuid: "" + row.id }));
      usageRows.push();

      if (usage.next) {
        offset = offset + usage.results.length;
        setProgress((offset / usage.count) * 100);
        await new Promise((r) => setTimeout(r, 1000));
      } else {
        setProgress(0);
        fetchedAllUsages = true;
      }
    }
    return usageRows;
  };

  const onClick = async () => {
    setExporting(true);

    const rows = await fetchAllUsageRows();

    // @ts-ignore  Sadly write-excel-file's typescript doesn't handle the case of multiple schemas
    await writeXlsxFile([rows], {
      fileName: "HCC usage overview.xlsx",
      schema: [excelFileSchema],
      fontFamily: "Montserrat", // Like the site
      dateFormat: "yyyy-mm-dd hh:mm",
      stickyRowsCount: 1, // Header
      sheets: ["Usage"],
    });

    setExporting(false);
  };

  return (
    <Button
      variant="primary"
      leftIcon={<AddIcon boxSize={5} />}
      title="Export to Excel file"
      onClick={onClick}
      isDisabled={disabled || exporting}
      {...rest}
    >
      <Text overflow="hidden" textOverflow="ellipsis" w="100%" m="0">
        Export to Excel file
      </Text>
      {exporting ? <ExportLoadingModal progress={progress} isOpen={exporting} /> : null}
    </Button>
  );
};

export default UsageExcelButton;
